import { RefObject, useEffect, useMemo } from 'react';

import { BUSINESS_UNIT } from '@zola-helpers/client/dist/es/tracking/onboard/onboardTrackingConstants';
import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';

import cx from 'classnames';
import LazyLoad from 'react-lazyload';

import CachedImage from '~/components/common/images/CachedImage';
import { WeddingOptionFacetView } from '~/types/responseTypes';
import {
  TrackingProductLocation,
  TrackingProductType,
  trackProductClicked,
} from '~/util/trackingHelper';

import ColorTagsList from '../ColorTagsList/ColorTagsList';

import styles from './WeddingAlbumCard.module.less';

const trackWeddingClicked = (
  photoId: string,
  position: number,
  location: TrackingProductLocation,
  productType: TrackingProductType,
  transactionId?: string,
  name = 'Wedding'
) => {
  trackProductClicked({
    location,
    productId: photoId,
    name: name || 'Wedding',
    position,
    productType,
    businessUnit: BUSINESS_UNIT.REAL_WEDDINGS,
    transactionId,
  });
};

export type WeddingAlbumCardProps = {
  colors?: WeddingOptionFacetView[]; // TODO: need to pass this down as well
  imageUuid: string;
  title?: string;
  slug?: string;
  trackingLocation?: TrackingProductLocation;
  trackingType?: TrackingProductType;
  trackingTransactionId?: string;
  uuid: string;
  index: number;
  carousel?: boolean;
  imageWidth?: number;
  restorationRef?: RefObject<HTMLDivElement>;
  onPersistScroll?: () => void;
  className?: string;
  trackClicked?: () => void;
  lazyLoad?: boolean;
};

export const WeddingAlbumCard = ({
  uuid,
  title,
  slug,
  colors = [],
  imageUuid,
  trackingLocation,
  trackingType,
  trackingTransactionId,
  index,
  imageWidth,
  restorationRef,
  carousel,
  onPersistScroll,
  className,
  trackClicked,
  lazyLoad = false,
}: WeddingAlbumCardProps): JSX.Element => {
  const props = carousel ? {} : { width: imageWidth, height: 220 };
  const pathname = `/inspiration/wedding/${slug}`;

  useEffect(() => {
    if (!restorationRef || !restorationRef.current) return;
    restorationRef?.current?.scrollIntoView({ behavior: 'auto', block: 'center' });
  }, []);

  const linkProps = {
    href: pathname,
    onClick: () => {
      if (trackClicked) {
        trackClicked();
      } else if (trackingLocation && trackingType) {
        trackWeddingClicked(
          uuid,
          index + 1,
          trackingLocation,
          trackingType,
          trackingTransactionId,
          title
        );
      }
      if (onPersistScroll) {
        onPersistScroll();
      }
    },
  };

  const coverImage = useMemo(
    () => <CachedImage uuid={imageUuid} alt="" fit="crop" {...props} />,
    [imageUuid, props]
  );

  return (
    <div
      className={cx(styles.container, { [styles.carouselCard]: carousel }, className)}
      data-testid="WeddingAlbumCard"
      ref={restorationRef}
    >
      <div className={styles.photoContainer}>
        <LinkV2 {...linkProps}>
          {lazyLoad ? (
            <LazyLoad height={225} offset={300} once>
              {coverImage}
            </LazyLoad>
          ) : (
            coverImage
          )}
        </LinkV2>
      </div>
      <div className={styles.metadataContainer}>
        <div className={styles.weddingName}>
          <LinkV2 {...linkProps} noTextTransform className={styles.weddingTitleLink}>
            {title}
          </LinkV2>
        </div>
        <div className={styles.colors}>
          {colors && <ColorTagsList colors={colors} classes={styles.colorDot} compact />}
        </div>
      </div>
    </div>
  );
};

export default WeddingAlbumCard;
